import API from '../api/api';
import env from '../config/environments';

// Proctoring telemetry capture for interview sessions.
//
// Absolute rule: telemetry must never interfere with the interview. Every
// public method and event handler is wrapped so it cannot throw into interview
// code, every send is fire-and-forget, and after MAX_FAILURES consecutive
// flush failures capture disables itself for the rest of the page load.
// Input activity is captured as counts only — key identities, typed text, and
// pointer coordinates are never read.
// Nothing is emitted until the interview is started (armed): setup and practice
// screens run before consent/start, and pre-start flushes would 404 on the
// profile endpoint and burn the failure budget.

const MAX_EVENTS_PER_BATCH = 1000;
const MAX_BATCH_BYTES = 120 * 1024;
const CHUNK_SIZE = 400;
const CHECKPOINT_MS = 45000;
const MOUSE_SAMPLE_MS = 250;
const BUCKET_SECONDS = 10;
const MAX_FAILURES = 3;

const uuid = () => {
    try {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) { /* fall through */ }
    return `p-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

// One id per page load, shared by every batch this tab sends
const SESSION_ID = uuid();

const noop = () => {};
const NOOP_PROCTOR = {
    attachLifecycle: noop,
    teardown: noop,
    interviewStarted: noop,
    interviewEnded: noop,
    reloadDetected: noop,
    markExpectedUnload: noop,
    startQuestion: noop,
    questionSubmitted: noop,
    stopCapture: noop,
    deviceChanged: noop,
    mediaTrackEvent: noop
};

export const createInterviewProctor = (endpointPath) => {
    // No proctor endpoint configured for this interview type → capture is a no-op
    if (!endpointPath) return NOOP_PROCTOR;

    const beaconUrl = `${env.candidateApiUrl.replace(/\/+$/, '')}/${endpointPath.replace(/^\/+/, '')}`;

    let disabled = false;
    let failures = 0;

    // True once the interview is known started server-side; all emission is
    // dropped (not buffered) before then
    let armed = false;

    // Set just before an intentional reload (post-submit transition): the
    // imminent pagehide must still beacon-flush buffers, but must not emit a
    // page-unload event
    let expectedUnload = false;

    // StrictMode double-invokes the mount effect in dev, so the interruption
    // branch can call reloadDetected() twice on the same proctor instance.
    // Per-instance scope dedupes that while a real remount (new proctor)
    // correctly emits again.
    let reloadReported = false;

    let capturing = false;
    let questionNumber = null;
    let anchorMs = 0;
    let questionEvents = [];
    let sessionEvents = [];
    let sessionFlushTimer = null;
    let checkpointTimer = null;
    let removeQuestionListeners = null;
    let removePageLifecycle = null;

    // Input accumulators — deltas since the last summary emission, so
    // checkpoint summaries never double-count buckets
    let kbBuckets = new Map();
    let mouseBuckets = new Map();
    let activeMouseSeconds = new Set();
    let leftViewportCount = 0;
    let lastEmitT = 0;
    let lastMouseSampleMs = 0;

    const safe = (fn) => (...args) => {
        try { return fn(...args); } catch (e) { return undefined; }
    };

    const elapsed = () => Math.round((performance.now() - anchorMs) / 10) / 100;

    const clientInfo = () => ({
        user_agent: navigator.userAgent || '',
        screen_width: (window.screen && window.screen.width) || 0,
        screen_height: (window.screen && window.screen.height) || 0,
        window_width: window.innerWidth || 0,
        window_height: window.innerHeight || 0
    });

    const disable = () => {
        disabled = true;
        capturing = false;
        detachQuestionListeners();
        if (checkpointTimer) { clearInterval(checkpointTimer); checkpointTimer = null; }
        if (sessionFlushTimer) { clearTimeout(sessionFlushTimer); sessionFlushTimer = null; }
        questionEvents = [];
        sessionEvents = [];
    };

    const registerFailure = () => {
        failures += 1;
        if (failures >= MAX_FAILURES) disable();
    };

    const pushQuestionEvent = (category, action, meta = {}) => {
        if (disabled || !capturing) return;
        questionEvents.push({ t: elapsed(), at: null, category, action, meta });
    };

    const pushSessionEvent = (category, action, meta = {}) => {
        if (disabled || !armed) return;
        sessionEvents.push({ t: null, at: new Date().toISOString(), category, action, meta });
        if (!sessionFlushTimer) {
            sessionFlushTimer = setTimeout(() => {
                sessionFlushTimer = null;
                flushSessionEvents(false);
            }, 1000);
        }
    };

    const dropOldestByCategory = (events, category, excess) => {
        const kept = [];
        for (const event of events) {
            if (excess > 0 && event.category === category) { excess -= 1; continue; }
            kept.push(event);
        }
        return kept;
    };

    // Server cap is a hard reject: trim oldest input summaries first, then
    // device events, and only then newest-wins on the rest
    const enforceEventCap = (events) => {
        for (const category of ['input', 'device']) {
            if (events.length <= MAX_EVENTS_PER_BATCH) return events;
            events = dropOldestByCategory(events, category, events.length - MAX_EVENTS_PER_BATCH);
        }
        return events.length > MAX_EVENTS_PER_BATCH ? events.slice(-MAX_EVENTS_PER_BATCH) : events;
    };

    const sendBatch = (batchQuestionNumber, events, useBeacon) => {
        const batch = {
            batch_id: uuid(),
            session_id: SESSION_ID,
            question_number: batchQuestionNumber,
            events,
            client: clientInfo()
        };
        if (JSON.stringify(batch).length > MAX_BATCH_BYTES) {
            batch.events = batch.events.filter((event) => event.category !== 'input');
        }
        if (useBeacon && navigator.sendBeacon) {
            // text/plain avoids a CORS preflight; the server parses JSON regardless
            const body = new Blob([JSON.stringify(batch)], { type: 'text/plain' });
            try {
                if (!navigator.sendBeacon(beaconUrl, body)) registerFailure();
            } catch (e) {
                registerFailure();
            }
            return;
        }
        API.post(endpointPath, batch, { skipAuthRedirect: true })
            .then(() => { failures = 0; })
            .catch(() => { registerFailure(); });
    };

    const flushEvents = (batchQuestionNumber, events, useBeacon) => {
        if (disabled || events.length === 0) return;
        let remaining = enforceEventCap(events);
        while (remaining.length > 0) {
            sendBatch(batchQuestionNumber, remaining.slice(0, CHUNK_SIZE), useBeacon);
            remaining = remaining.slice(CHUNK_SIZE);
        }
    };

    const flushQuestionEvents = (useBeacon) => {
        const events = questionEvents;
        questionEvents = [];
        flushEvents(questionNumber, events, useBeacon);
    };

    const flushSessionEvents = (useBeacon) => {
        const events = sessionEvents;
        sessionEvents = [];
        flushEvents(null, events, useBeacon);
    };

    // Buckets are 10s-aligned but clipped to the emission window
    // [lastEmitT, windowEndT], so consecutive summaries never overlap
    const drainBuckets = (buckets, windowEndT) => {
        const t0s = [];
        const t1s = [];
        const counts = [];
        let total = 0;
        for (const index of Array.from(buckets.keys()).sort((a, b) => a - b)) {
            const count = buckets.get(index);
            t0s.push(Math.max(index * BUCKET_SECONDS, lastEmitT));
            t1s.push(Math.min((index + 1) * BUCKET_SECONDS, windowEndT));
            counts.push(count);
            total += count;
        }
        buckets.clear();
        // meta values must be scalars or flat lists of scalars, so buckets are
        // encoded as parallel arrays rather than a list of objects
        return { bucket_t0: t0s, bucket_t1: t1s, bucket_counts: counts, total };
    };

    const emitInputSummaries = () => {
        if (disabled || !capturing) return;
        const windowEndT = elapsed();
        const windowSeconds = Math.max(0, windowEndT - lastEmitT);
        pushQuestionEvent('input', 'keyboard-summary', drainBuckets(kbBuckets, windowEndT));
        const mouse = drainBuckets(mouseBuckets, windowEndT);
        mouse.idle_seconds = Math.max(0, Math.round(windowSeconds - activeMouseSeconds.size));
        mouse.left_viewport_count = leftViewportCount;
        pushQuestionEvent('input', 'mouse-summary', mouse);
        activeMouseSeconds.clear();
        leftViewportCount = 0;
        lastEmitT = windowEndT;
    };

    const attachQuestionListeners = () => {
        let lastFullscreen = !!document.fullscreenElement;

        const onVisibility = safe(() => {
            pushQuestionEvent('focus', document.visibilityState === 'hidden' ? 'tab-hidden' : 'tab-visible');
        });
        const onBlur = safe(() => pushQuestionEvent('focus', 'window-blur'));
        const onFocus = safe(() => pushQuestionEvent('focus', 'window-focus'));
        const onFullscreen = safe(() => {
            const isFullscreen = !!document.fullscreenElement;
            if (isFullscreen === lastFullscreen) return;
            lastFullscreen = isFullscreen;
            pushQuestionEvent('focus', isFullscreen ? 'fullscreen-enter' : 'fullscreen-exit');
        });
        // Count only — the key pressed is never read
        const onKeyDown = safe(() => {
            const bucket = Math.floor(elapsed() / BUCKET_SECONDS);
            kbBuckets.set(bucket, (kbBuckets.get(bucket) || 0) + 1);
        });
        // Sampled presence only — pointer coordinates are never read
        const onPointerMove = safe(() => {
            const nowMs = performance.now();
            if (nowMs - lastMouseSampleMs < MOUSE_SAMPLE_MS) return;
            lastMouseSampleMs = nowMs;
            const t = elapsed();
            const bucket = Math.floor(t / BUCKET_SECONDS);
            mouseBuckets.set(bucket, (mouseBuckets.get(bucket) || 0) + 1);
            activeMouseSeconds.add(Math.floor(t));
        });
        const onMouseLeave = safe(() => { leftViewportCount += 1; });

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);
        document.addEventListener('fullscreenchange', onFullscreen);
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('pointermove', onPointerMove, true);
        document.addEventListener('mouseleave', onMouseLeave);

        removeQuestionListeners = () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('fullscreenchange', onFullscreen);
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('pointermove', onPointerMove, true);
            document.removeEventListener('mouseleave', onMouseLeave);
        };
    };

    const detachQuestionListeners = () => {
        if (removeQuestionListeners) {
            try { removeQuestionListeners(); } catch (e) { /* ignore */ }
            removeQuestionListeners = null;
        }
    };

    const startCapture = (qNumber, recovered) => {
        if (disabled) return;
        if (capturing) endCapture(false);
        armed = true;
        capturing = true;
        questionNumber = qNumber;
        anchorMs = performance.now();
        questionEvents = [];
        kbBuckets = new Map();
        mouseBuckets = new Map();
        activeMouseSeconds = new Set();
        leftViewportCount = 0;
        lastEmitT = 0;
        lastMouseSampleMs = 0;
        attachQuestionListeners();
        pushQuestionEvent('session', 'question-started', { question_number: qNumber, recovered: !!recovered });
        checkpointTimer = setInterval(safe(() => {
            emitInputSummaries();
            flushQuestionEvents(false);
        }), CHECKPOINT_MS);
    };

    const endCapture = (useBeacon) => {
        if (!capturing) return;
        emitInputSummaries();
        detachQuestionListeners();
        if (checkpointTimer) { clearInterval(checkpointTimer); checkpointTimer = null; }
        flushQuestionEvents(useBeacon);
        capturing = false;
        questionNumber = null;
    };

    const recordDeviceEvent = (action, meta) => {
        if (capturing) pushQuestionEvent('device', action, meta);
        else pushSessionEvent('device', action, meta);
    };

    return {
        attachLifecycle: safe(() => {
            if (removePageLifecycle) return;
            const onPageHide = safe(() => {
                if (disabled || !armed) return;
                if (!expectedUnload) {
                    sessionEvents.push({ t: null, at: new Date().toISOString(), category: 'session', action: 'page-unload', meta: {} });
                }
                expectedUnload = false;
                if (capturing) {
                    emitInputSummaries();
                    flushQuestionEvents(true);
                }
                flushSessionEvents(true);
            });
            window.addEventListener('pagehide', onPageHide);
            removePageLifecycle = () => window.removeEventListener('pagehide', onPageHide);
        }),
        teardown: safe(() => {
            endCapture(false);
            if (sessionFlushTimer) { clearTimeout(sessionFlushTimer); sessionFlushTimer = null; }
            flushSessionEvents(false);
            if (removePageLifecycle) {
                try { removePageLifecycle(); } catch (e) { /* ignore */ }
                removePageLifecycle = null;
            }
        }),
        interviewStarted: safe(() => {
            armed = true;
            pushSessionEvent('session', 'interview-started');
        }),
        interviewEnded: safe(() => pushSessionEvent('session', 'interview-ended')),
        reloadDetected: safe((qNumber) => {
            // Only ever called for an already-started interview
            armed = true;
            if (reloadReported) return;
            reloadReported = true;
            pushSessionEvent('session', 'reload-detected', qNumber != null ? { question_number: qNumber } : {});
        }),
        markExpectedUnload: safe(() => { expectedUnload = true; }),
        startQuestion: safe(startCapture),
        questionSubmitted: safe((autoSubmit) => {
            if (!capturing) return;
            pushQuestionEvent('session', 'question-submitted', { question_number: questionNumber, auto_submit: !!autoSubmit });
            // Beacon: the post-submit reload follows immediately and would abort
            // an in-flight XHR, and the reload must never wait on telemetry
            endCapture(true);
            flushSessionEvents(true);
        }),
        stopCapture: safe(() => endCapture(false)),
        deviceChanged: safe((kind) => recordDeviceEvent('device-changed', { kind })),
        mediaTrackEvent: safe((action, kind) => recordDeviceEvent(action, { kind }))
    };
};
